import { useMemo, ReactNode } from 'react';
import { Text, Button, Colors, Icon } from '@mercator-gcp/grout';
import type { ButtonProps } from '@mercator-gcp/grout';
import type { SavedStateColorScheme } from 'components/widgets/SaveProject/savedStateColors';

const basicStyle = {
  padding: 'padding_8',
  transition: 'background-color 0.2s ease',
  'span:last-child': {
    color: 'transparent',
    transition: 'color 0.1s ease',
  },
  _hover: {
    backgroundColor: 'transparent',
    '>span:last-child': {
      color: Colors.Black800,
    },
  },
  _focus: {
    backgroundColor: 'transparent',
    '>span:last-child': {
      color: Colors.Black800,
    },
  },
};

const hoverStyle = {
  '&:hover, &:focus': {
    padding: 'padding_8',
    backgroundColor: Colors.ArmyGreen200,
  },
};

const hoverCircleStyle = {
  ...hoverStyle,
  borderRadius: '50%',
};

const savedButtonStyle = {
  ...basicStyle,
  border: '1px solid',
  borderColor: Colors.ArmyGreen200,
  padding: 'padding_8',
  justifyContent: 'flex-start',
  backgroundColor: Colors.ArmyGreen100,
  '&:hover, &:focus': {
    border: '1px solid',
    borderColor: Colors.ArmyGreen200,
    padding: 'padding_8',
    backgroundColor: Colors.ArmyGreen200,
  },
};

type TSavedProjectButtonProps = {
  variant: 'styled' | 'unstyled';
  sx?: ButtonProps['sx'];
  onClick: ButtonProps['onClick'];
  label: string;
  icon: ReactNode;
  colorScheme?: SavedStateColorScheme;
};

const SavedProjectButton = ({
  variant = 'styled',
  sx = {},
  onClick,
  label,
  icon,
  colorScheme,
}: TSavedProjectButtonProps) => {
  const savedProjectStyle = useMemo(() => {
    if (variant === 'unstyled') {
      if (label) {
        return { ...basicStyle, ...hoverStyle, ...sx };
      }

      return { ...basicStyle, ...hoverCircleStyle, ...sx };
    }

    if (colorScheme) {
      const schemeStyle = {
        padding: 'padding_8',
        borderRadius: '4px',
        transition: 'background-color 0.2s ease',
        border: '1px solid',
        borderColor: colorScheme.border,
        justifyContent: 'flex-start',
        backgroundColor: colorScheme.background,
        color: colorScheme.foreground,
        // Keep every labeled state button the same size regardless of its label.
        ...(label ? { width: '160px', whiteSpace: 'nowrap' } : {}),
        '&:hover, &:focus': {
          border: '1px solid',
          borderColor: colorScheme.border,
          backgroundColor: colorScheme.background,
        },
      };

      return { ...schemeStyle, ...sx };
    }

    return { ...savedButtonStyle, ...sx };
  }, [sx, variant, label, colorScheme]);

  if (label) {
    return (
      <Button
        sx={savedProjectStyle}
        variant="unstyled"
        onClick={onClick}
        startAdornment={icon}
        endAdornment={
          <Icon name={Icon.Name.ChevronDownFilled} fill={colorScheme?.foreground} />
        }
      >
        <Text.BodyBold
          sx={{
            color: colorScheme?.foreground,
            // Fill the fixed-width button so the chevron stays pinned to the right edge.
            ...(colorScheme ? { flexGrow: 1, textAlign: 'left' } : {}),
          }}
        >
          {label}
        </Text.BodyBold>
      </Button>
    );
  }

  return (
    <Button sx={savedProjectStyle} variant="unstyled" onClick={onClick}>
      {icon}
    </Button>
  );
};

export default SavedProjectButton;
